<!--
  - Copyright (C) 2020. Archimedes Exhibitions GmbH,
  - Saarbrücker Str. 24, Berlin, Germany
  -
  - This file contains proprietary source code and confidential
  - information. Its contents may not be disclosed or distributed to
  - third parties unless prior specific permission by Archimedes
  - Exhibitions GmbH, Berlin, Germany is obtained in writing. This applies
  - to copies made in any form and using any medium. It applies to
  - partial as well as complete copies.
  -->

<template>
  <div>
    <b-modal
      :id="'nodesToLink-' + compId"
      size="xl"
      scrollable
      :title="nodeListTitle"
      :hide-footer="!editIsBatchPick"
      @ok="onLinkModalOk"
    >
      <list-view
        ref="linkNodeList"
        version="published"
        :allow-editing="false"
        :language="node.language"
        :schemas-to-load="linkedSchemas"
        :allow-batch-pick="editIsBatchPick"
        :black-list="editBlackList"
        v-on:row-clicked="onLinkNodeClicked"
      >
      </list-view>
    </b-modal>

    <b-form>
      <b-form-group
        class="fieldset-border-right"
        v-for="field in schema.fields"
        :key="field.name"
        :disabled="isFieldDisabled(field)"
      >
        <label>
          {{ $t('cms.common.' + field.name) + getAllowLabel(field) + ':' }}
        </label>
        <div class="float-right" v-if="getCharsLeft(field.name) != null">
          <b-badge :variant="getCharsLeft(field.name) < 0 ? 'danger' : null">
            {{ getCharsLeft(field.name) }}
          </b-badge>
        </div>
        <div
          class="float-right"
          v-else-if="
            field.type === 'string' && getCharCount(field.name) != null
          "
        >
          <b-badge>
            {{ getCharCount(field.name) }}
          </b-badge>
        </div>

        <b-alert :show="!fulfillsUniqueness(field)" variant="danger">
          {{ $t('cms.nodeFields.unique') }}
        </b-alert>

        <div
          v-if="
            field.type === 'string' &&
            !field.allow &&
            !isColorField(schema, field)
          "
        >
          <b-form-input
            v-if="isSingleLineField(schema, field)"
            v-model="node_.fields[field.name]"
            :required="field.required"
            :state="getFieldState(field)"
            :placeholder="getPlaceholder(field)"
          >
          </b-form-input>
          <b-form-textarea
            v-else
            v-model="node_.fields[field.name]"
            rows="3"
            :required="field.required"
            :state="getFieldState(field)"
            :placeholder="getPlaceholder(field)"
          >
          </b-form-textarea>
        </div>

        <b-form-input
          v-else-if="
            field.type === 'string' &&
            !field.allow &&
            isColorField(schema, field)
          "
          v-model="node_.fields[field.name]"
          :required="field.required"
          :state="getFieldState(field)"
          type="color"
        >
        </b-form-input>

        <b-form-input
          v-else-if="field.type === 'number' && !field.allow"
          v-model="node_.fields[field.name]"
          type="number"
          :required="field.required"
          :state="getFieldState(field)"
          :placeholder="getPlaceholder(field)"
          @change="
            () => {
              node_.fields[field.name] =
                node_.fields[field.name] === ''
                  ? null
                  : node_.fields[field.name]
            }
          "
          number
        >
        </b-form-input>

        <b-form-input
          v-else-if="field.type === 'date' && !field.allow"
          v-model="node_.fields[field.name]"
          :type="isTimeField(schema, field) ? 'time' : 'date'"
          :required="field.required"
          :state="getFieldState(field)"
          :placeholder="getPlaceholder(field)"
        >
        </b-form-input>

        <b-form-select
          v-else-if="
            field.type === 'string' || (field.type === 'number' && field.allow)
          "
          :options="field.allow"
          v-model="node_.fields[field.name]"
        >
        </b-form-select>

        <!-- field.type === 'binary' -->

        <b-card
          v-if="field.type === 'binary'"
          :border-variant="!isFieldValid(field) ? 'danger' : null"
          class="text-center"
        >
          <div
            class="media-container"
            @drop.prevent="(e) => onFileDroped(e, field.name)"
          >
            <div v-if="!isFieldValid(field)" class="float-right">
              <b-icon-exclamation-circle
                variant="danger"
              ></b-icon-exclamation-circle>
            </div>
            <div
              v-if="
                node_.fields[field.name] &&
                (node_.fields[field.name].preview ||
                  node_.fields[field.name].binaryUuid) &&
                !field.required
              "
              class="float-right"
            >
              <button
                type="button"
                class="close"
                aria-label="Close"
                @click="wipeBinaryField(field.name)"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div
              v-if="
                node_.fields[field.name] && node_.fields[field.name].fileName
              "
            >
              <b-icon
                :icon="getIconType(node_.fields[field.name].mimeType)"
              ></b-icon>
              {{ node_.fields[field.name].fileName }}
            </div>
            <div class="mb-1">
              <span
                v-if="
                  node_.fields[field.name] && node_.fields[field.name].mimeType
                "
                class="small text-muted"
                >Type: {{ node_.fields[field.name].mimeType }}</span
              >
              <span
                v-if="
                  node_.fields[field.name] && node_.fields[field.name].fileSize
                "
                class="small text-muted"
                >, Size:
                {{ makeHumanReadable(node_.fields[field.name].fileSize) }}</span
              >
              <span
                v-if="
                  node_.fields[field.name] && node_.fields[field.name].width
                "
                class="small text-muted"
                >, Width: {{ node_.fields[field.name].width }}</span
              >
              <span
                v-if="
                  node_.fields[field.name] && node_.fields[field.name].height
                "
                class="small text-muted"
                >, Height: {{ node_.fields[field.name].height }}</span
              >
            </div>
            <b-img
              v-if="
                node_.fields[field.name] &&
                node_.fields[field.name].mimeType &&
                node_.fields[field.name].mimeType.match('image')
              "
              :src="node_.fields[field.name].preview"
              center
            ></b-img>
            <div
              v-else-if="
                node_.fields[field.name] &&
                node_.fields[field.name].mimeType &&
                node_.fields[field.name].mimeType.match('video')
              "
              class="embed-responsive embed-responsive-16by9"
            >
              <video controls center :key="node_.fields[field.name].preview">
                <source
                  :src="node_.fields[field.name].preview"
                  :type="node_.fields[field.name].mimeType"
                />
              </video>
            </div>
            <div
              v-else-if="
                node_.fields[field.name] &&
                node_.fields[field.name].mimeType &&
                node_.fields[field.name].mimeType.match('audio')
              "
              class="mt-4"
            >
              <audio
                controls
                :src="node_.fields[field.name].preview"
                :type="node_.fields[field.name].mimeType"
              ></audio>
            </div>
            <div v-else class="mt-3">
              <b-icon-cloud-upload font-scale="2"></b-icon-cloud-upload>
              <br />
              <b>{{ $t('cms.uploader.drop') }}</b>
            </div>
            <input
              type="file"
              :ref="field.name"
              style="display: none"
              @change="(e) => onFileInput(e, field.name)"
              :multiple="!node"
            />
            <b-button
              size="sm"
              variant="dark"
              class="mt-3 mb-1"
              @click="$refs[field.name][0].click()"
            >
              <b-icon-folder2-open></b-icon-folder2-open>
              {{ $t('cms.uploader.browse') }}
            </b-button>
          </div>
        </b-card>

        <!-- field.type === 'node' -->

        <div v-else-if="field.type === 'node'">
          <div class="mb-2">
            <b-button
              class="mr-2"
              size="sm"
              variant="primary"
              @click="showNodesToLink(field.name, false)"
            >
              <b-icon-folder-symlink></b-icon-folder-symlink>
              {{ $t('cms.nodeFields.select') }}
            </b-button>

            <b-button
              v-if="getAllowedSchemas(field.name).length == 1"
              size="sm"
              class="mr-2"
              variant="primary"
              @click="
                onCreateLinkedNode(field.name, getAllowedSchemas(field.name)[0])
              "
            >
              <b-icon-plus></b-icon-plus>
              {{ $t('cms.nodeFields.create') }}
            </b-button>

            <b-dropdown v-else class="mr-2" size="sm" variant="primary">
              <template slot="button-content">
                <b-icon-plus></b-icon-plus>
                {{ $t('cms.nodeFields.create') }}
              </template>
              <b-dropdown-item
                class="my-2"
                v-for="(schema, i) in getAllowedSchemas(field.name)"
                :key="i"
                @click="onCreateLinkedNode(field.name, schema)"
              >
                <b-icon
                  v-if="$cmsManager.getSchemaIcon(schema)"
                  :icon="$cmsManager.getSchemaIcon(schema)"
                  class="mr-1"
                >
                </b-icon>
                {{ $cmsManager.getSchemaAlias(schema) }}
              </b-dropdown-item>
            </b-dropdown>

            <b-alert
              class="my-2"
              variant="info"
              :show="!isFieldValid(field) ? true : false"
            >
              {{ $t('cms.nodeFields.minimum') }}
            </b-alert>
          </div>

          <div v-if="node_.fields[field.name]">
            <b-card no-body class="linked-node overflow-hidden px-1">
              <b-row
                no-gutters
                v-if="
                  node_.fields[field.name] &&
                  node_.fields[field.name].displayName
                "
              >
                <b-col class="d-flex flex-grow-0">
                  <b-card-img
                    v-if="getThumbnail(node_.fields[field.name])"
                    :src="getThumbnail(node_.fields[field.name])"
                    :alt="
                      getTruncatedString(node_.fields[field.name].displayName)
                    "
                    class="rounded-2 mr-3"
                    :id="`img-popover-${node_.uuid}-${compId}`"
                  ></b-card-img>
                  <b-popover
                    v-if="getThumbnail(node_)"
                    :target="`img-popover-${node_.uuid}-${compId}`"
                    triggers="hover"
                  >
                    <b-img
                      fluid
                      :src="getThumbnail(node_, 300, null, false)"
                      :alt="getTruncatedString(node_.displayName)"
                      height="300"
                    >
                    </b-img>
                  </b-popover>
                </b-col>
                <b-col cols="6">
                  <ul class="list-unstyled">
                    <li>
                      <span
                        :id="`title-popover-${
                          node_.fields[field.name].uuid
                        }-${compId}`"
                      >
                        {{
                          getTruncatedString(
                            node_.fields[field.name].displayName
                          )
                        }}</span
                      >
                      <b-popover
                        placement="right"
                        :target="`title-popover-${
                          node_.fields[field.name].uuid
                        }-${compId}`"
                        triggers="hover focus"
                      >
                        <div
                          v-for="(f_, i) in $cmsManager.getSchemaWithName(
                            node_.fields[field.name].schema.name
                          ).fields"
                          :key="i"
                        >
                          <small
                            v-if="
                              f_.type === 'string' &&
                              node_.fields[field.name].fields[f_.name]
                            "
                          >
                            <b>{{ $t('cms.common.' + f_.name) }}: </b>
                            {{
                              getTruncatedString(
                                node_.fields[field.name].fields[f_.name],
                                255
                              )
                            }}
                          </small>
                          <small
                            v-else-if="
                              f_.type === 'date' &&
                              node_.fields[field.name].fields[f_.name]
                            "
                          >
                            <b>{{ $t('cms.common.' + f_.name) }}: </b>
                            {{
                              formatDate(
                                node_.fields[field.name].fields[f_.name],
                                false
                              )
                            }}
                          </small>
                        </div>
                      </b-popover>
                    </li>
                    <li>
                      <b-badge variant="dark" class="mx-1">
                        <b-icon
                          v-if="
                            $cmsManager.getSchemaIcon(
                              node_.fields[field.name].schema.name
                            )
                          "
                          :icon="
                            $cmsManager.getSchemaIcon(
                              node_.fields[field.name].schema.name
                            )
                          "
                        >
                        </b-icon>
                        {{
                          $cmsManager.getSchemaAlias(
                            node_.fields[field.name].schema.name
                          )
                        }}
                      </b-badge>
                      <b-badge
                        class="mx-1"
                        v-if="isChanged(node_.fields[field.name])"
                        variant="primary"
                      >
                        {{ $t('cms.common.changed').toUpperCase() }}
                      </b-badge>
                      <b-badge
                        class="mx-1"
                        variant="warning"
                        v-if="!isPublished(node_.fields[field.name])"
                      >
                        {{ $t('cms.nodeFields.draftWarning').toUpperCase() }}
                      </b-badge>
                    </li>
                  </ul>
                </b-col>
                <b-col class="text-right">
                  <b-button
                    class="mr-1 mb-1"
                    variant="dark"
                    size="sm"
                    @click="onEditLinkedNode(node_.fields[field.name])"
                  >
                    <b-icon-pencil></b-icon-pencil>
                  </b-button>
                  <b-button
                    class="mr-1 mb-1"
                    variant="outline-dark"
                    size="sm"
                    @click="node_.fields[field.name] = null"
                  >
                    <b-icon-arrow-bar-right></b-icon-arrow-bar-right>
                  </b-button>
                </b-col>
              </b-row>
              <b-spinner v-else></b-spinner>
            </b-card>
          </div>
        </div>

        <div v-else-if="field.type === 'list' && field.listType === 'string'">
          <b-row>
            <b-col>
              <b-button
                class="mb-2"
                size="sm"
                variant="secondary"
                @click="addSimpleItemToList(field.name)"
                block
              >
                <b-icon-plus></b-icon-plus>
                {{ $t('cms.nodeFields.addText') }}
              </b-button>
            </b-col>
            <b-col></b-col>
          </b-row>
          <div v-if="node_.fields[field.name]">
            <draggable
              v-model="node_.fields[field.name]"
              @start="drag = true"
              @end="drag = false"
              :options="{ disabled: isFieldDisabled(field) }"
            >
              <b-card
                :style="isFieldDisabled(field) ? null : 'cursor: -webkit-grab;'"
                no-body
                class="overflow-hidden px-1 mb-1"
                v-for="(text, i) in node_.fields[field.name]"
                :key="i"
              >
                <b-row>
                  <b-col cols="1">
                    <b-badge class="ml-2 mt-2">{{ i + 1 }}.</b-badge>
                  </b-col>
                  <b-col cols="10">
                    <b-form-textarea
                      class="mt-2 ml-2 mb-2"
                      trim
                      v-model="node_.fields[field.name][i]"
                      rows="1"
                      max-rows="6"
                      :value="text"
                    ></b-form-textarea>
                  </b-col>
                  <b-col cols="1">
                    <button
                      type="button"
                      class="close"
                      aria-label="Close"
                      @click="deleteItemFromList(i, field.name)"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </b-col>
                </b-row>
              </b-card>
            </draggable>
          </div>
        </div>

        <!-- field.type === 'list' -->

        <div v-else-if="field.type === 'list' && field.listType === 'node'">
          <div class="mb-2">
            <b-button
              class="mr-2"
              size="sm"
              variant="primary"
              @click="showNodesToLink(field.name)"
            >
              <b-icon-folder-symlink></b-icon-folder-symlink>
              {{ $t('cms.nodeFields.select') }}
            </b-button>

            <b-button
              v-if="getAllowedSchemas(field.name).length == 1"
              class="mr-2"
              size="sm"
              variant="primary"
              @click="
                onCreateLinkedNode(field.name, getAllowedSchemas(field.name)[0])
              "
            >
              <b-icon-plus></b-icon-plus>
              {{ $t('cms.nodeFields.create') }}
            </b-button>

            <b-dropdown v-else class="mr-2" size="sm" variant="primary">
              <template slot="button-content">
                <b-icon-plus></b-icon-plus>
                {{ $t('cms.nodeFields.create') }}
              </template>
              <b-dropdown-item
                class="my-2"
                v-for="(schema, i) in getAllowedSchemas(field.name)"
                :key="i"
                @click="onCreateLinkedNode(field.name, schema)"
              >
                <b-icon
                  v-if="$cmsManager.getSchemaIcon(schema)"
                  :icon="$cmsManager.getSchemaIcon(schema)"
                  class="mr-1"
                >
                </b-icon>
                {{ $cmsManager.getSchemaAlias(schema) }}
              </b-dropdown-item>
            </b-dropdown>

            <b-alert
              class="my-2"
              variant="info"
              :show="!isFieldValid(field) ? true : false"
            >
              {{ $t('cms.nodeFields.minimum') }}
            </b-alert>
          </div>

          <div class="text-right" v-if="node_.fields[field.name]">
            <small>
              {{ $t('cms.common.listItems') }}
              ({{ node_.fields[field.name].length }})
            </small>
          </div>

          <div v-if="node_.fields[field.name]">
            <draggable
              v-model="node_.fields[field.name]"
              @start="drag = true"
              @end="drag = false"
              :options="{ disabled: isFieldDisabled(field) }"
            >
              <b-card
                :style="isFieldDisabled(field) ? null : 'cursor: -webkit-grab;'"
                no-body
                class="linked-node overflow-hidden px-1"
                v-for="(n_, i) in node_.fields[field.name]"
                :key="i"
              >
                <b-row no-gutters v-if="n_ && n_.displayName">
                  <b-col class="d-flex flex-grow-0">
                    <b-card-img
                      v-if="getThumbnail(n_)"
                      :src="getThumbnail(n_)"
                      :alt="getTruncatedString(n_.displayName)"
                      class="rounded-2 mr-3"
                      :id="`listimg-popover-${n_.uuid}-${compId}`"
                    >
                      >
                    </b-card-img>
                    <b-popover
                      v-if="getThumbnail(n_)"
                      :target="`listimg-popover-${n_.uuid}-${compId}`"
                      triggers="hover"
                      placement="right"
                    >
                      <b-img
                        :src="getThumbnail(n_, 300, null, false)"
                        :alt="getTruncatedString(n_.displayName)"
                        style="width: 100%"
                        class="my-1"
                      >
                      </b-img>
                    </b-popover>
                  </b-col>
                  <b-col cols="6">
                    <ul class="list-unstyled">
                      <li>
                        <span :id="`title-popover-${n_.uuid}-${compId}`">
                          {{ getTruncatedString(n_.displayName) }}</span
                        >
                        <b-popover
                          :target="`title-popover-${n_.uuid}-${compId}`"
                          triggers="hover"
                        >
                          <div
                            v-for="(field, i) in $cmsManager.getSchemaWithName(
                              n_.schema.name
                            ).fields"
                            :key="i"
                          >
                            <small
                              v-if="
                                field.type === 'string' && n_.fields[field.name]
                              "
                            >
                              <b>{{ $t('cms.common.' + field.name) }}:</b>
                              {{
                                getTruncatedString(n_.fields[field.name], 255)
                              }}
                            </small>
                            <small
                              v-else-if="
                                field.type === 'date' && n_.fields[field.name]
                              "
                            >
                              <b>{{ $t('cms.common.' + field.name) }}:</b>
                              {{ formatDate(n_.fields[field.name], false) }}
                            </small>
                          </div>
                        </b-popover>
                      </li>
                      <li>
                        <b-badge variant="dark" class="mx-1">
                          <b-icon
                            v-if="$cmsManager.getSchemaIcon(n_.schema.name)"
                            :icon="$cmsManager.getSchemaIcon(n_.schema.name)"
                          ></b-icon>
                          {{ $cmsManager.getSchemaAlias(n_.schema.name) }}
                        </b-badge>
                        <b-badge
                          class="mx-1"
                          v-if="isChanged(n_)"
                          variant="primary"
                        >
                          {{ $t('cms.common.changed').toUpperCase() }}
                        </b-badge>
                        <b-badge
                          class="mx-1"
                          variant="warning"
                          v-if="!isPublished(n_)"
                        >
                          {{ $t('cms.nodeFields.draftWarning').toUpperCase() }}
                        </b-badge>
                      </li>
                    </ul>
                  </b-col>
                  <b-col class="text-right">
                    <b-button
                      class="mr-1 mb-1"
                      variant="dark"
                      size="sm"
                      @click="onEditLinkedNode(n_)"
                    >
                      <b-icon-pencil></b-icon-pencil>
                    </b-button>
                    <b-button
                      class="mr-1 mb-1"
                      variant="outline-dark"
                      size="sm"
                      @click="deleteItemFromList(i, field.name)"
                    >
                      <b-icon-arrow-bar-right></b-icon-arrow-bar-right>
                    </b-button>
                  </b-col>
                </b-row>
                <b-spinner v-else></b-spinner>
              </b-card>
            </draggable>
          </div>
        </div>
      </b-form-group>
    </b-form>
  </div>
</template>

<script>
  import { utils as libUtils } from '@amdx/emslib'
  import utils from '../js/utils'

  export default {
    name: 'NodeField',
    components: {
      draggable: () => import('vuedraggable'),
      ListView: () => import('../views/ListView')
    },

    props: {
      isReadOnly: null,
      schema: null,
      node: null,
      language: null,
      uniqueTitles: null,
      thumbSize: {
        default: 90
      },
      blackLists: null
    },

    data () {
      return {
        node_: null,
        nodeListTitle: null,
        linkedSchemas: null,
        linkField: null,
        editUUID: null,
        editLanguage: null,
        editSchema: null,
        editParentUUID: null,
        editBlackList: [],
        objectURLs: [],
        editIsBatchPick: true,
        compId: null
      }
    },

    computed: {
      fieldsValidity () {
        return this.areFieldsValid()
      }
    },

    watch: {
      node: {
        immediate: true,
        deep: true,
        handler (newVal, _) {
          this.revokeObjectURLs()

          let node = JSON.parse(JSON.stringify(this.node))
          let schema = this.$cmsManager.getSchemaWithName(node.schema.name)

          if (node.uuid) {
            for (let field of schema.fields) {
              if (field.type === 'binary') {
                if (
                  node.fields[field.name] &&
                  node.fields[field.name].binaryUuid
                ) {
                  node.fields[field.name].preview = utils.getBinaryURL(
                    node,
                    field.name,
                    null,
                    600,
                    false
                  )
                  node.fields[field.name].upload = null
                } else {
                  node.fields[field.name] = {
                    preview: null,
                    upload: null
                  }
                }
              } else if (field.type === 'date' && node.fields[field.name]) {
                let date = node.fields[field.name].split('T')
                if (this.isTimeField(schema, field)) {
                  let time = date[1].split(':')
                  node.fields[field.name] = time[0] + ':' + time[1]
                } else {
                  node.fields[field.name] = date[0]
                }
              }
            }
          }
          this.node_ = node

          this.$emit('validityChanged', this.fieldsValidity)
        }
      },
      fieldsValidity () {
        this.$emit('validityChanged', this.fieldsValidity)
      }
    },

    methods: {
      revokeObjectURLs () {
        for (let url of this.objectURLs) {
          try {
            URL.revokeObjectURL(url)
          } catch (e) {
            console.log(e)
          }
        }
        this.objectURLs = []
      },

      isFieldDisabled (field) {
        if (
          this.isReadOnly ||
          (this.language !== this.$cmsManager.defaultLanguage &&
            this.$cmsManager.isFieldNonTranslated(this.schema.name, field.name))
        ) {
          return true
        }

        return false
      },

      isFieldSet (field) {
        if (this.node_ && field.name in this.node_.fields) {
          if (field.type === 'binary' && this.node_.fields[field.name]) {
            return (
              !!this.node_.fields[field.name].preview ||
              !!this.node_.fields[field.name].binaryUuid
            )
          }

          if (!this.fulfillsUniqueness(field)) {
            return false
          }

          if (field.type === 'list' && this.node_.fields[field.name]) {
            return this.node_.fields[field.name].length > 0
          }

          return (
            !!this.node_.fields[field.name] ||
            this.node_.fields[field.name] === 0
          )
        }
        return false
      },

      fulfillsUniqueness (field) {
        if (
          this.uniqueTitles &&
          field.name === this.schema.displayField &&
          this.node_.fields[field.name]
        ) {
          return !this.uniqueTitles.find(
            (u) =>
              u.title === this.node_.fields[field.name] &&
              u.uuid !== this.node_.uuid
          )
        }
        return true
      },

      isFieldValid (field) {
        if (!this.isFieldDisabled(field)) {
          let isSet = this.isFieldSet(field)
          if (field.required && !isSet) {
            return false
          }

          let charsLeft = this.getCharsLeft(field.name)
          if (charsLeft != null && charsLeft < 0) {
            return false
          }
        }
        return true
      },

      getFieldState (field) {
        return !this.isFieldValid(field) && !this.isFieldDisabled(field)
          ? false
          : null
      },

      getPlaceholder (field) {
        return !this.isFieldValid(field)
          ? this.$t('cms.nodeFields.fieldIsRequired')
          : null
      },

      getAllowLabel (field) {
        if (field.allow) {
          let allowed = []
          for (let a of field.allow) {
            let alias = this.$cmsManager.getSchemaAlias(a)
            if (alias) {
              allowed.push(alias)
            } else {
              allowed.push(a)
            }
          }
          return ' (' + allowed.join(', ') + ')'
        }
        return ''
      },

      areFieldsValid () {
        if (this.schema && this.node) {
          for (let field of this.schema.fields) {
            if (!this.isFieldValid(field)) {
              return false
            }
          }
          return true
        }
        return false
      },

      addSimpleItemToList (fieldName) {
        if (this.node_.fields[fieldName]) {
          let list = this.node_.fields[fieldName]
          list.push(null)
          this.$set(this.node_.fields, fieldName, list)
        } else {
          this.$set(this.node_.fields, fieldName, [null])
        }
      },

      wipeBinaryField (fieldName) {
        let fields = Object.assign({}, this.node_.fields)
        fields[fieldName] = {
          preview: null,
          upload: null
        }
        this.$set(this.node_, 'fields', fields)
      },

      deleteItemFromList (listIndex, fieldName) {
        this.node_.fields[fieldName].splice(listIndex, 1)
      },

      getTruncatedString (string, maxLength = 30) {
        if (string) {
          return libUtils.getTruncatedString(string, maxLength)
        }
        return null
      },

      getThumbnail (node, w = this.thumbSize, h = this.thumbSize, crop = true) {
        return utils.getPreview(node, w, h, crop)
      },

      getNode () {
        let schema = this.$cmsManager.getSchemaWithName(this.node_.schema.name)
        for (let field of schema.fields) {
          if (field.type === 'date' && this.node_.fields[field.name]) {
            let finalDate
            if (this.isTimeField(schema, field)) {
              let time = this.node_.fields[field.name].split(':')
              finalDate = new Date(Date.UTC(2000, 1, 1, time[0], time[1]))
            } else {
              let date = this.node_.fields[field.name].split('-')
              finalDate = new Date(Date.UTC(date[0], date[1], date[2], 12, 0))
              finalDate = new Date(this.node_.fields[field.name] + ' 12:00')
            }
            this.node_.fields[field.name] = finalDate.toISOString()
          } else if (field.type === 'date' && !this.node_.fields[field.name]) {
            this.node_.fields[field.name] = null
          }
        }
        return this.node_
      },

      getCmsRoute (node) {
        let routeName =
          this.$cmsManager.getContainerName(node.parentNode.uuid) + '-edit'
        return {
          name: routeName,
          params: { uuid: node.uuid },
          query: {
            lang: node.language,
            parentUuid: node.parentNode.uuid,
            schemaName: node.schema.name
          }
        }
      },

      getIconType (mimeType) {
        if (mimeType) {
          if (mimeType.includes('image')) {
            return 'file-image'
          }
          if (mimeType.includes('audio')) {
            return 'file-music'
          }
          if (mimeType.includes('video')) {
            return 'file-play'
          }
        }
        return 'file-binary'
      },

      isPublished (node) {
        if (node.languages) {
          let langInfo = node.languages.find(
            (d) => d.language === node.language
          )
          let status = utils.getPublishStatus(langInfo)
          return (
            status === utils.publishStatus.PUBLISHED ||
            status === utils.publishStatus.CHANGED
          )
        }
        return false
      },

      isChanged (node) {
        if (node.languages) {
          let langInfo = node.languages.find(
            (d) => d.language === node.language
          )
          let status = utils.getPublishStatus(langInfo)
          return status === utils.publishStatus.CHANGED
        }
        return false
      },

      getBlackList (fieldName) {
        let field = this.schema.fields.find((f) => f.name === fieldName)
        if (
          field.type !== 'node' &&
          field.type !== 'list' &&
          field.lisType !== 'node'
        ) {
          return []
        }

        let blackList = []
        if (this.blackLists && this.blackLists[fieldName]) {
          blackList = this.blackLists[fieldName]
        }
        if (this.node_.uuid) {
          blackList.push(this.node_.uuid)
        }
        if (
          field.type === 'node' &&
          this.node_.fields[fieldName] &&
          this.node_.fields[fieldName].uuid
        ) {
          blackList.push(this.node_.fields[fieldName].uuid)
        } else if (
          field.type === 'list' &&
          field.listType === 'node' &&
          this.node_.fields[fieldName]
        ) {
          for (let node of this.node_.fields[fieldName]) {
            blackList.push(node.uuid)
          }
        }
        return [...new Set(blackList)]
      },

      getBlackLists () {
        let blackLists = {}
        for (let fieldName in this.node_.fields) {
          let blackList = this.getBlackList(fieldName)
          if (blackList.length) {
            blackLists[fieldName] = blackList
          }
        }
        return blackLists
      },

      showNodesToLink (fieldName, isBatchPick = true) {
        this.editBlackList = this.getBlackList(fieldName)
        this.editIsBatchPick = isBatchPick
        this.linkedSchemas = this.getAllowedSchemas(fieldName)
        this.linkField = fieldName
        this.nodeListTitle = this.$t('cms.nodeFields.linkTo', {
          title: fieldName
        })
        this.$bvModal.show('nodesToLink-' + this.compId)
      },

      getAllowedSchemas (fieldName) {
        let field = this.schema.fields.find((f) => f.name === fieldName)
        if (field.allow) {
          let allow = [...field.allow]
          allow.sort()
          return allow
        }
        return null
      },

      getCharsLeft (fieldName) {
        let maxChars = this.$cmsManager.getTextLength(
          this.schema.name,
          fieldName
        )
        if (maxChars != null) {
          if (this.node_.fields[fieldName]) {
            return maxChars - this.node_.fields[fieldName].length
          }
          return maxChars
        }
        return null
      },

      getCharCount (fieldName) {
        if (this.node_.fields[fieldName]) {
          return this.node_.fields[fieldName].length
        }
        return null
      },

      createPreview (fieldName, file) {
        for (let key in this.$cmsManager.bulkUploadContainers.assets) {
          const data = this.$cmsManager.bulkUploadContainers.assets[key]
          if (
            data.schema === this.node_.schema.name &&
            data.previewField &&
            fieldName === data.field
          ) {
            utils
              .createThumbnail(file)
              .then((preview) => {
                this.handleFiles([preview], data.previewField)
              })
              .catch((error) => {
                libUtils.toast(this, 'Could not create preview file!', error)
              })
            return
          }
        }
      },

      handleFiles (files, fieldName) {
        if (files.length > 1) {
          libUtils.toast(this, 'Use one file only!', 'To many files!')
          return
        }
        let file = files[0]
        let uploadTypes = this.$cmsManager.getUploadTypes(
          this.node_.schema.name,
          fieldName
        )
        if (uploadTypes && !uploadTypes.includes(file.type)) {
          libUtils.toast(
            this,
            'Wrong file type: ' + file.type + '!',
            'This field only accepts: ' + uploadTypes.join(', ')
          )
          return
        }
        this.node_.fields[fieldName].preview = URL.createObjectURL(file)
        this.node_.fields[fieldName].upload = file
        this.node_.fields[fieldName].mimeType = file.type
        this.node_.fields[fieldName].fileName = file.name
        this.$forceUpdate()
        // Create a preview image if video file is added
        this.createPreview(fieldName, file)
      },

      formatDate (dateStr, includeTime = true) {
        if (dateStr) {
          return utils.formatDate(dateStr, includeTime)
        }
        return null
      },

      isColorField (schema, field) {
        return this.$cmsManager.isColorField(schema.name, field.name)
      },

      isTimeField (schema, field) {
        return this.$cmsManager.isTimeField(schema.name, field.name)
      },

      isSingleLineField (schema, field) {
        return this.$cmsManager.isSingleLineField(schema.name, field.name)
      },

      makeHumanReadable (number) {
        return utils.humanFileSize(number, true)
      },

      onLinkNodeClicked (node) {
        let field = this.schema.fields.find((f) => f.name === this.linkField)

        if (field.type === 'list') {
          let nodes = []
          if (this.node_.fields[this.linkField]) {
            nodes = this.node_.fields[this.linkField]
          }
          nodes.push(node)
          this.$set(this.node_.fields, this.linkField, nodes)
        } else {
          this.$set(this.node_.fields, this.linkField, node)
        }

        this.$bvModal.hide('nodesToLink-' + this.compId)
      },

      onLinkModalOk () {
        let nodes = []
        if (this.node_.fields[this.linkField]) {
          nodes = this.node_.fields[this.linkField]
        }
        nodes.push(...this.$refs.linkNodeList.getPickedNodes())
        this.$set(this.node_.fields, this.linkField, nodes)
      },

      onCreateLinkedNode (fieldName, schemaName) {
        let field = this.schema.fields.find((f) => f.name === fieldName)
        this.linkedSchemas = field.allow
        this.linkField = fieldName
        this.editUUID = 'new'
        this.editLanguage = this.node.language
        this.editSchema = schemaName
        this.editParentUUID =
          this.$cmsManager.getParentUUIDForSchema(schemaName)

        this.$emit(
          'edit-linked-node',
          this,
          this.editUUID,
          this.editLanguage,
          this.editSchema,
          this.editParentUUID,
          this.getBlackLists()
        )
      },

      onEditLinkedNode (node) {
        this.editUUID = node.uuid
        this.editLanguage = node.language

        this.$emit(
          'edit-linked-node',
          this,
          this.editUUID,
          this.editLanguage,
          node.schema.name,
          null,
          this.getBlackLists()
        )
      },

      onEditNodeUpdate (node) {
        if (this.editUUID === 'new') {
          let field = this.schema.fields.find((f) => f.name === this.linkField)
          if (field.type === 'list') {
            let nodes = []
            if (this.node_.fields[this.linkField]) {
              nodes = this.node_.fields[this.linkField]
            }
            nodes.push(node)
            this.$set(this.node_.fields, this.linkField, nodes)
          } else {
            this.$set(this.node_.fields, this.linkField, node)
          }

          this.editUUID = node.uuid
        } else {
          for (let key in this.node_.fields) {
            let data = this.node_.fields[key]
            if (data) {
              if (data.uuid === node.uuid) {
                this.node_.fields[key] = node
              } else if (Array.isArray(data)) {
                for (let i in data) {
                  let item = data[i]
                  if (item.uuid === node.uuid) {
                    data[i] = node
                  }
                }
              }
            }
          }
          this.$forceUpdate()
        }
      },

      onFileDroped (e, fieldName) {
        let pickedFiles = e.dataTransfer.files
        this.handleFiles(pickedFiles, fieldName)
      },

      onFileInput (e, fieldName) {
        let pickedFiles = e.target.files
        this.handleFiles(pickedFiles, fieldName)
        e.target.value = null
      }
    },

    created () {
      this.compId = utils.uuidv4()
    },

    beforeDestroy () {
      this.revokeObjectURLs()
    }
  }
</script>

<style lang="scss" scoped>
  .linked-node.card {
    padding: 0.1em;
    margin: 3px 0;
    border: 1px dashed #d8d8d8;
    background-color: #f8f9fa;

    .row {
      display: flex;
      align-items: center;
    }

    img {
      width: 50px;
    }

    .no-gutters {
      margin-right: 10px;
      margin-left: 5px;
    }

    .list-unstyled {
      padding-top: 5px;
    }
  }

  .media-container {
    img {
      max-width: 100%;
      max-height: 500px;
    }
  }

  .fieldset-border-right {
    border-left: 3px solid #cfd9e0;
    margin-bottom: 40px;
    padding-left: 1em;
    padding-bottom: 0.1em;

    label {
      font-weight: bold;
      color: #414d63;
    }

    .card {
      border-radius: 6px;
    }
  }
</style>
